import { vars } from "nativewind";
import { PrimaryPalette } from "../utils/derive-palette";

export type ThemeVars = Record<string, string>;

/**
 * Plain hex maps (NOT wrapped in vars()).
 *
 * IMPORTANT: on native, nativewind's `vars()` returns an opaque empty object
 * whose data lives in an internal WeakMap keyed by that exact reference.
 * Spreading a `vars()` result (`{ ...vars(...) }`) therefore loses the data on
 * native (it only "worked" on web because react-native-web keeps the raw keys).
 * So we keep RAW maps here, expose them for direct value reads
 * (`themeVars["--color-x"]`), and call `vars()` exactly once at the point we
 * apply them as a style (see theme-provider / makeThemeStyle).
 */
const lightColors: ThemeVars = {
  "--color-primary-default": "#0669B3",
  "--color-primary-1": "#BAE0FD",
  "--color-primary-2": "#EBF6FE",
  "--color-primary-3": "#EBF6FE",
  "--color-onyx-default": "#22272a",
  "--color-chalk-default": "#FFFFFF",
  "--color-obsidian-default": "#F2F4F6",
  "--color-ash-default": "#f2f4f6",
  "--color-ash-1": "#8597b2",
  "--color-ash-2": "#607DA1",
  "--color-ash-3": "#DFE5EC",
  "--color-slate-default": "#526581",
  "--color-crimson-default": "#F04438",
  "--color-crimson-1": "#E90C0C",
  "--color-jade-default": "#27AE60",
  "--color-blush-default": "#F6DDE6",
  "--color-lavender-default": "#E9E1F2",
  "--color-wheat-default": "#F6ECD8",
  "--color-coral-default": "#F6DDD4",
  "--color-sage-default": "#EDF2E1",
  "--color-shadow-light": "rgba(82, 101, 129, 0.08)",
  "--color-shadow-dark": "rgba(34, 39, 42, 0.18)",
  "--color-shadow-darker": "rgba(34, 39, 42, 0.5)",
};

const darkColors: ThemeVars = {
  "--color-primary-default": "#0669B3",
  "--color-primary-1": "#BAE0FD",
  "--color-primary-2": "#EBF6FE",
  "--color-primary-3": "#0d2d45",
  "--color-onyx-default": "#D7DADC",
  "--color-crimson-default": "#F04438",
  "--color-crimson-1": "#E90C0C",
  "--color-jade-default": "#27AE60",
  "--color-chalk-default": "#181e24",
  "--color-obsidian-default": "#0f1113",
  "--color-ash-default": "#1e242c",
  "--color-ash-1": "#6a7a90",
  "--color-ash-2": "#8090a8",
  "--color-ash-3": "#2d3540",
  "--color-slate-default": "#8aaac8",
  "--color-blush-default": "#3d2028",
  "--color-lavender-default": "#2a2040",
  "--color-wheat-default": "#3d3018",
  "--color-coral-default": "#3d2218",
  "--color-sage-default": "#1e2d18",
  "--color-shadow-light": "rgba(0, 0, 0, 0.18)",
  "--color-shadow-dark": "rgba(0, 0, 0, 0.40)",
  "--color-shadow-darker": "rgba(34, 39, 42, 0.5)",
};

/** Raw (plain) color maps per scheme. */
export const themes: Record<"light" | "dark", ThemeVars> = {
  light: lightColors,
  dark: darkColors,
};

/** Plain map of the primary-palette overrides for a scheme. */
export const paletteToVars = (
  palette: PrimaryPalette,
  scheme: "light" | "dark",
): ThemeVars => ({
  "--color-primary-default": palette.default,
  "--color-primary-1": scheme === "dark" ? palette["1-dark"] : palette[1],
  "--color-primary-2": scheme === "dark" ? palette["2-dark"] : palette[2],
});

/** Merged plain hex map for the given scheme + palette (for value reads). */
export const buildThemeVars = (
  scheme: "light" | "dark",
  palette: PrimaryPalette,
): ThemeVars => ({
  ...themes[scheme],
  ...paletteToVars(palette, scheme),
});

/**
 * Build the style that actually registers the CSS variables with nativewind.
 * Always call `vars()` here (never spread its result) so it works on native.
 */
export const makeThemeStyle = (
  scheme: "light" | "dark",
  palette: PrimaryPalette,
) => vars(buildThemeVars(scheme, palette));
